import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Home from "./pages/Home";
import { AuthProvider } from "./hooks/useAuth";

// Lazy-load non-critical routes to shrink the initial JS bundle
// and reduce main-thread blocking time / Max Potential FID.
const Subjects = lazy(() => import("./pages/Subjects"));
const SubjectDetail = lazy(() => import("./pages/SubjectDetail"));
const Tutorials = lazy(() => import("./pages/Tutorials"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Mission = lazy(() => import("./pages/Mission"));
const Materiale = lazy(() => import("./pages/Materiale"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AdsLanding = lazy(() => import("./pages/AdsLanding"));
const EducatorsLanding = lazy(() => import("./pages/EducatorsLanding"));
const Community = lazy(() => import("./pages/Community"));
const QuestionDetail = lazy(() => import("./pages/QuestionDetail"));
const News = lazy(() => import("./pages/News"));

const ModuleOverview = lazy(() => import("./pages/ModuleOverview"));
const Lesson = lazy(() => import("./pages/Lesson"));
const Auth = lazy(() => import("./pages/Auth"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Dev = lazy(() => import("./pages/Dev"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Returns = lazy(() => import("./pages/legal/Returns"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="se-theme"
      disableTransitionOnChange
    >
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/subjects" element={<Subjects />} />
            <Route path="/subjects/:slug" element={<SubjectDetail />} />
            <Route path="/subjects/romanian/module/:moduleSlug" element={<ModuleOverview />} />
            <Route path="/subjects/romanian/module/:moduleSlug/lesson/:lessonId" element={<Lesson />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/about" element={<About />} />
            <Route path="/misiune" element={<Mission />} />
            <Route path="/materiale" element={<Materiale />} />
            <Route path="/resurse" element={<Navigate to="/materiale?tab=biblioteca" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/start" element={<AdsLanding />} />
            <Route path="/bac" element={<AdsLanding />} />
            <Route path="/profesori" element={<EducatorsLanding />} />
            <Route path="/alumni" element={<EducatorsLanding />} />
            <Route path="/comunitate-educatori" element={<EducatorsLanding />} />
            <Route path="/community" element={<Community />} />
            <Route path="/community/:id" element={<QuestionDetail />} />
            <Route path="/news" element={<News />} />
            <Route path="/bac-arhiva" element={<Navigate to="/materiale?tab=arhiva" replace />} />
            <Route path="/arhiva" element={<Navigate to="/materiale?tab=arhiva" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/check-email" element={<CheckEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dev" element={<Dev />} />
            <Route path="/legal/confidentialitate" element={<Privacy />} />
            <Route path="/legal/termeni" element={<Terms />} />
            <Route path="/legal/returnare" element={<Returns />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
